import { Box, Grid } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  buildDefaultValues,
  DynamicForm,
  Header,
  LwFormInput,
  LwFormNumberInput,
  Page,
  Toolbox,
} from 'redesign';
import { useTranslation } from 'react-i18next';
import { formatFullName } from 'shared/utils/formatting';
import { Section } from 'shared/components/common/form-section';
import { EditDocumentFormContentProps, EditDocumentFormData } from './edit-document-content.types';
import { EditDocumentFormToolbox } from '../edit-document-form-toolbox/edit-document-form-toolbox';
import { DocumentLanguageSelect } from '../../commons/components/document-language-select';

export const EditDocumentFormContent = ({
  formSchema,
  selectedDocumentModel,
  documentData,
}: EditDocumentFormContentProps) => {
  const { t } = useTranslation('hq-screen-specific');
  const formMethods = useForm<EditDocumentFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: formatFullName({
        firstName: documentData.firstName,
        lastName: documentData.lastName,
        infix: documentData.infix,
      }),
      contractHours: documentData.contractMinutes
        ? Math.round((documentData.contractMinutes / 60) * 10000) / 10000
        : undefined,
      language: documentData.language,
      ...buildDefaultValues(selectedDocumentModel.items),
    },
  });

  return (
    <FormProvider {...formMethods}>
      <Page
        header={<Header titleText={t('document_edit.page_title')} />}
        toolbox={
          <Toolbox>
            <EditDocumentFormToolbox currentLanguage={documentData.language} />
          </Toolbox>
        }
      >
        <Box width={'50%'}>
          <Grid container>
            <Section
              sectionLabel={t('document_form.document_details_section_title', {
                ns: 'hq-screen-specific',
              })}
            >
              <Grid item xs={12}>
                <LwFormInput
                  control={formMethods.control}
                  name="name"
                  label={t('document_edit.form.name_label')}
                  disabled
                />
                <LwFormNumberInput
                  control={formMethods.control}
                  name="contractHours"
                  label={t('worker_data.contract_hours', { ns: 'all-platforms-common' })}
                  disabled
                />
              </Grid>
              <DocumentLanguageSelect availableLanguages={documentData.availableLanguages} />
            </Section>
            <DynamicForm config={selectedDocumentModel} />
          </Grid>
        </Box>
      </Page>
    </FormProvider>
  );
};
